import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { ContentBlockStreamService } from "../../../../services/content-block-stream.service";
import { RenderModeRegistry } from "../../../../services/render-mode.registry";
import { DebugModeService } from "../../../../services/debug-mode.service";
import { ToolFrontendExecutorComponent } from "../tool-frontend-executor/tool-frontend-executor.component";
import { CardGridComponent } from "../../shared/card-grid/card-grid.component";
import { ContentBlockCardComponent } from "../../cards/content-block-card/content-block-card.component";
import { EndpointCardComponent } from "../../cards/endpoint-card/endpoint-card.component";
import { TaskListCardComponent } from "../../cards/task-list-card/task-list-card.component";
import { DeepAnalysisMessageComponent } from "../../cards/task-list-card/deep-analysis-message/deep-analysis-message.component";
import { PlanCardComponent } from "../../cards/plan-card/plan-card.component";
import { MapLayerCardComponent } from "../../cards/map-layer-card/map-layer-card.component";
import { GeoHintChipComponent } from "../../inline/geo-hint-chip/geo-hint-chip.component";
import { WorkareaHintChipComponent } from "../../inline/workarea-hint-chip/workarea-hint-chip.component";
import { CHIP_CONTENT_KINDS } from "../../../../model/block.types";
import { resolveLayerContract } from "../../../../model/evidence-layer.types";
import type {
  NormalizedContentBlock,
  ContentBlockData,
  ApiEndpointData,
  ToolFrontendData,
  BackgroundJobBlockData,
  PlanData,
} from "../../../../model/block.types";

/**
 * Central dispatcher for all content blocks in a single AI message.
 *
 * Layout:
 * - tool_frontend blocks: rendered as hidden ToolFrontendExecutorComponent (no-op, deprecated)
 * - content blocks (geometry/geolocation/place/geotiff/image/tabular/structured/layer_spec/
 *   message/blob) / api_endpoint / background_job / plan: rendered in a responsive Bootstrap
 *   grid (1 / 2 / 3 cols) — content-blocks S5 unified all evidence results (including former
 *   geocoding/places_result/geo_buffer inline chips) into a single card renderer.
 *
 * Stability invariant: blocks only grow and update their status — they never disappear.
 * LangGraph emits intermediate STATE_SNAPSHOT events during tool execution where a message's
 * non_standard content blocks may be absent. That grow-only accumulation (empty parse keeps
 * the previous blocks, merge by id with last-wins duplicates, never remove) lives in
 * ContentBlockStreamService — the stream layer (chat-snapshot-stream-reducer S3). This
 * component is a pure READER: it renders whatever `blocksFor(messageId)` returns. No
 * component effect and no data reduction here: component effects run inside Angular's
 * change-detection cycle, and the former in-effect snapshot reduction fed the NG0103
 * infinite change-detection loop.
 */
@Component({
  selector: "app-content-dispatcher",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ToolFrontendExecutorComponent,
    CardGridComponent,
    ContentBlockCardComponent,
    EndpointCardComponent,
    TaskListCardComponent,
    DeepAnalysisMessageComponent,
    PlanCardComponent,
    MapLayerCardComponent,
    GeoHintChipComponent,
    WorkareaHintChipComponent,
  ],
  templateUrl: "./content-dispatcher.component.html",
  styleUrl: "./content-dispatcher.component.scss",
})
export class ContentDispatcherComponent {
  /** ID of the message to render blocks for. */
  messageId = input<string>("");

  /** Stream-layer store of per-message accumulated blocks (chat-snapshot-stream-reducer S3). */
  readonly #stream = inject(ContentBlockStreamService);

  readonly #renderRegistry = inject(RenderModeRegistry);

  protected readonly debugMode = inject(DebugModeService);

  constructor() {
  }

  /** Accumulated blocks of this message — reduced in the stream layer, read-only here. */
  protected readonly allBlocks = computed(() => this.#stream.blocksFor(this.messageId()));

  /**
   * A block is shown once, under the first message that carried it (an uploaded file stays
   * under the user's message instead of repeating beside the turn's results). The plan card
   * is exempt: the final message carries it again with the plan's current state.
   */
  protected readonly visibleBlocks = computed(() => {
    const dm = this.debugMode.isDebugMode();
    const messageId = this.messageId();
    return this.allBlocks().filter((b) => {
      if (!this.#isPlanBlock(b) && this.#stream.ownerOf(b.id) !== messageId) return false;
      const config = this.#renderRegistry.getConfig(b.kind, this.#toolName(b));
      return !config.debugOnly || dm;
    });
  });

  protected readonly silentBlocks = computed(() =>
    this.visibleBlocks().filter(
      (b) => this.#renderRegistry.getConfig(b.kind, this.#toolName(b)).renderMode === "silent",
    ),
  );

  /** The plan card first, evidence results after it — the results close the message. */
  protected readonly gridBlocks = computed(() => {
    const cards = this.visibleBlocks().filter(
      (b) =>
        this.#renderRegistry.getConfig(b.kind, this.#toolName(b)).renderMode === "card" &&
        !this.#isGeoHintBlock(b) &&
        !this.#isWorkareaHintBlock(b),
    );
    return [...cards.filter((b) => this.#isPlanBlock(b)), ...cards.filter((b) => !this.#isPlanBlock(b))];
  });

  /** `geolocation`/`place` mcp_tool blocks — rendered as an inline chip row, excluded from the grid. */
  protected readonly geoHintBlocks = computed(() => this.visibleBlocks().filter((b) => this.#isGeoHintBlock(b)));

  /**
   * Plan-level workarea geometry blocks (evidence-layer-ui US5) — rendered as a compact hint chip
   * in the same inline chips row as the geo hints, excluded from the grid (user ruling: HINT, not
   * card). Detection is contract-based ONLY: `data.layer.type === "workarea"`; there is NO
   * id-prefix fallback.
   */
  protected readonly workareaHintBlocks = computed(() => this.visibleBlocks().filter((b) => this.#isWorkareaHintBlock(b)));

  // ── Type-safe data extractors (used in template) ──────────────────────────

  protected asContentBlockData(block: NormalizedContentBlock): ContentBlockData {
    return (block as Extract<NormalizedContentBlock, { data: ContentBlockData }>).data;
  }

  protected asApiEndpointData(block: NormalizedContentBlock): ApiEndpointData {
    return (block as Extract<NormalizedContentBlock, { kind: "api_endpoint" }>).data;
  }

  protected asToolFrontendData(block: NormalizedContentBlock): ToolFrontendData {
    return (block as Extract<NormalizedContentBlock, { kind: "tool_frontend" }>).data;
  }

  protected asBackgroundJobData(block: NormalizedContentBlock): BackgroundJobBlockData {
    return (block as Extract<NormalizedContentBlock, { kind: "background_job" }>).data;
  }

  protected asPlanData(block: NormalizedContentBlock): PlanData {
    return (block as Extract<NormalizedContentBlock, { kind: "plan" }>).data;
  }

  #toolName(block: NormalizedContentBlock): string | undefined {
    return block.kind === "tool_frontend" ? block.data.tool_name : undefined;
  }

  #isGeoHintBlock(block: NormalizedContentBlock): boolean {
    return CHIP_CONTENT_KINDS.has(block.kind);
  }

  #isPlanBlock(block: NormalizedContentBlock): boolean {
    return block.kind === "plan";
  }

  /**
   * True for the plan-level workarea geometry block (evidence-layer-ui US5): kind `geometry`
   * carrying a `data.layer` contract whose `type` is `workarea`. Detection is contract-based
   * ONLY — the legacy `workarea-` id-prefix detection is deleted (US5, no prefix fallback):
   * pre-contract blocks without `layer` render as regular geometry cards, with no workarea chip
   * and no add affordance.
   */
  #isWorkareaHintBlock(block: NormalizedContentBlock): boolean {
    return block.kind === "geometry" && resolveLayerContract(block.data)?.type === "workarea";
  }
}
